/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from 'react';
import {
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';
import { FaBox, FaArrowDown, FaArrowUp, FaPlus, FaMinus, FaSearch } from 'react-icons/fa';
import { UserContext } from '../../App';

// URL de base de l'API
const API_URL = process.env.REACT_APP_API_URL;

const InventoryPage = () => {
    const { user } = useContext(UserContext);
    const accessToken = localStorage.getItem('accessToken');

    const [summary, setSummary] = useState(null);
    const [products, setProducts] = useState([]);
    const [movementData, setMovementData] = useState([]);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [isInputModalOpen, setIsInputModalOpen] = useState(false);
    const [selectedProductName, setSelectedProductName] = useState('');
    const [isOutputModalOpen, setIsOutputModalOpen] = useState(false);
    const [inputQuantity, setInputQuantity] = useState('');
    const [outputQuantity, setOutputQuantity] = useState('');
    const [message, setMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    useEffect(() => {
        if (user && user.userType === 'supplier' && accessToken) {
            fetchInventorySummary();
            fetchProducts();
        }
    }, [user, accessToken]);

    useEffect(() => {
        if (products.length > 0 && selectedProductId === null) {
            setSelectedProductId(products[0].productId);
            setSelectedProductName(products[0].productName);
        }
    }, [products, selectedProductId]);

    useEffect(() => {
        if (products.length > 0 && selectedProductId === null) {
            setSelectedProductId(products[0].productId);
        }
    }, [products, selectedProductId]);

    useEffect(() => {
        if (selectedProductId) {
            fetchProductMovements(selectedProductId);
        }
    }, [selectedProductId, accessToken]);

    const createHeaders = () => ({
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
    });

    const fetchInventorySummary = async () => {
        try {
            const response = await fetch(`${API_URL}/suppliers/${user.userId}/inventory/summary`, {
                headers: createHeaders()
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            const summaryData = data.summary && data.summary.length > 0 ? data.summary[0] : {};
            setSummary({
                totalStock: Number(summaryData.totalStock) || 0,
                totalInputs: Number(summaryData.totalInputs) || 0,
                totalOutputs: Number(summaryData.totalOutputs) || 0
            });
        } catch (error) {
            // No console.error, as per request
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await fetch(`${API_URL}/suppliers/${user.userId}/inventory/products`, {
                headers: createHeaders()
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            if (data && Array.isArray(data.products)) {
                const productsWithNumberStock = data.products.map(p => ({
                    ...p,
                    stockQuantity: Number(p.stockQuantity) || 0
                }));
                setProducts(productsWithNumberStock);
            } else {
                setProducts([]);
            }
        } catch (error) {
            setProducts([]);
        }
    };

    const fetchProductMovements = async (productId) => {
        try {
            const response = await fetch(`${API_URL}/suppliers/${user.userId}/inventory/movements/${productId}`, {
                headers: createHeaders()
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            setMovementData(data.movements);
        } catch (error) {
            // No console.error, as per request
        }
    };

    const handleInput = async (e) => {
        e.preventDefault();
        setMessage('');

        if (!selectedProductId) {
            setMessage('Veuillez sélectionner un produit avant d\'ajouter du stock.');
            return;
        }

        const parsedQuantity = parseInt(inputQuantity);
        if (Number.isNaN(parsedQuantity) || parsedQuantity <= 0) {
            setMessage('Veuillez entrer une quantité valide et positive.');
            return;
        }

        try {
            const response = await fetch(`${API_URL}/suppliers/${user.userId}/inventory/input`, {
                method: 'POST',
                headers: createHeaders(),
                body: JSON.stringify({ productId: selectedProductId, quantity: parsedQuantity })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Une erreur inconnue est survenue.');
            }

            fetchInventorySummary();
            fetchProducts();
            fetchProductMovements(selectedProductId);
            setIsInputModalOpen(false);
            setInputQuantity('');
            setMessage('Entrée de stock ajoutée avec succès.');
        } catch (error) {
            setMessage(`Échec de l'ajout d'entrée: ${error.message}`);
        }
    };

    const handleOutput = async (e) => {
        e.preventDefault();
        setMessage('');

        if (!selectedProductId) {
            setMessage('Veuillez sélectionner un produit avant de retirer du stock.');
            return;
        }

        const parsedQuantity = parseInt(outputQuantity, 10);
        if (Number.isNaN(parsedQuantity) || parsedQuantity <= 0) {
            setMessage('Veuillez entrer une quantité valide et positive.');
            return;
        }

        try {
            const response = await fetch(`${API_URL}/suppliers/${user.userId}/inventory/output`, {
                method: 'POST',
                headers: createHeaders(),
                body: JSON.stringify({ productId: selectedProductId, quantity: parsedQuantity })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Une erreur inconnue est survenue.');
            }

            fetchInventorySummary();
            fetchProducts();
            fetchProductMovements(selectedProductId);
            setIsOutputModalOpen(false);
            setOutputQuantity('');
            setMessage('Sortie de stock ajoutée avec succès.');
        } catch (error) {
            setMessage(`Échec de l'ajout de sortie: ${error.message}`);
        }
    };

    const productNames = products.reduce((acc, p) => ({ ...acc, [p.productId]: p.productName }), {});

    const filteredProducts = products.filter(p =>
        p.productName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const pieData = [
        { name: 'Entrées', value: summary?.totalInputs || 0, fill: '#8884d8' },
        { name: 'Sorties', value: summary?.totalOutputs || 0, fill: '#82ca9d' },
    ];

    if (!user) {
        return <div className="p-8 text-center text-gray-500">Veuillez vous connecter pour accéder à cette page.</div>;
    }

    return (
        <div className="p-8 bg-gray-100 min-h-screen font-sans">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-6">Gestion de l'Inventaire 📈</h1>
            <p className="text-gray-600 mb-8">
                Visualisez l'état de votre inventaire, suivez les entrées et les sorties, et gérez les niveaux de stock.
            </p>

            {/* Cartes récapitulatives de l'inventaire */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4">
                    <span className="text-5xl text-indigo-500"><FaBox /></span>
                    <div>
                        <h2 className="text-lg font-semibold text-gray-700">Stock Total</h2>
                        <p className="text-3xl font-bold text-indigo-600 mt-2">{summary?.totalStock || '...'}</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4">
                    <span className="text-5xl text-green-500"><FaArrowDown /></span>
                    <div>
                        <h2 className="text-lg font-semibold text-gray-700">Total des Entrées</h2>
                        <p className="text-3xl font-bold text-green-600 mt-2">{summary?.totalInputs || '...'}</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4">
                    <span className="text-5xl text-red-500"><FaArrowUp /></span>
                    <div>
                        <h2 className="text-lg font-semibold text-gray-700">Total des Sorties</h2>
                        <p className="text-3xl font-bold text-red-600 mt-2">{summary?.totalOutputs || '...'}</p>
                    </div>
                </div>
            </div>

            {/* Section des graphiques */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Mouvements de Stock</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100} label>
                                {pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} />)}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Stock Par Produit</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={products}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="productName"
                                angle={-45}
                                textAnchor="end"
                                height={80}
                                interval={0}
                                style={{ fontSize: '12px' }}
                            />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="stockQuantity" fill="#8884d8" name="Stock Restant" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Section de gestion */}
            <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Gestion des Entrées/Sorties</h2>
                <div className="flex flex-col md:flex-row items-stretch md:items-center space-y-4 md:space-y-0 md:space-x-4 mb-6">
                    <label htmlFor="product-search" className="text-gray-700 font-medium whitespace-nowrap">Sélectionnez un produit:</label>
                    <div className="relative flex-grow">
                        <input
                            type="text"
                            id="product-search"
                            className="block w-full px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="Rechercher un produit..."
                            value={selectedProductName ? selectedProductName : searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setSelectedProductName('');
                                setIsDropdownOpen(true);
                            }}
                            onFocus={() => setIsDropdownOpen(true)}
                        />
                        <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 pointer-events-none">
                            <FaSearch />
                        </span>
                        {isDropdownOpen && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                {filteredProducts.length > 0 ? (
                                    filteredProducts.map(p => (
                                        <div
                                            key={p.productId}
                                            onClick={() => {
                                                setSelectedProductId(p.productId);
                                                setSelectedProductName(p.productName);
                                                setSearchQuery('');
                                                setIsDropdownOpen(false);
                                            }}
                                            className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                                        >
                                            {p.productName}
                                        </div>
                                    ))
                                ) : (
                                    <div className="px-4 py-2 text-gray-500">Aucun produit trouvé.</div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex space-x-4">
                        <button
                            onClick={() => setIsInputModalOpen(true)}
                            className="px-6 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
                        >
                            <FaPlus />
                            <span>Ajouter</span>
                        </button>
                        <button
                            onClick={() => setIsOutputModalOpen(true)}
                            className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex items-center justify-center space-x-2"
                        >
                            <FaMinus />
                            <span>Retirer</span>
                        </button>
                    </div>
                </div>
                {message && <div className="text-red-500 mt-4 text-center">{message}</div>}
            </div>

            {/* Tableau de l'historique des mouvements */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Historique des Mouvements</h2>
                <div className="overflow-x-auto">
                    <table className="min-w-full table-auto">
                        <thead>
                            <tr className="bg-gray-50 text-gray-600 uppercase text-sm font-semibold">
                                <th className="py-3 px-6 text-left">Date</th>
                                <th className="py-3 px-6 text-left">Produit</th>
                                <th className="py-3 px-6 text-left">Type de Mouvement</th>
                                <th className="py-3 px-6 text-right">Quantité</th>
                            </tr>
                        </thead>
                        <tbody className="text-gray-600 text-sm font-light">
                            {movementData.length > 0 ? (
                                movementData.map(move => (
                                    <tr key={move.movementId} className="border-b border-gray-200 hover:bg-gray-50">
                                        <td className="py-3 px-6">{new Date(move.movementDate).toLocaleString()}</td>
                                        <td className="py-3 px-6">{productNames[move.productId]}</td>
                                        <td className="py-3 px-6">
                                            <span className={`py-1 px-3 rounded-full text-xs font-medium ${move.movementType === 'input' ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                                                }`}>
                                                {move.movementType === 'input' ? 'Entrée' : 'Sortie'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-6 text-right">{move.quantity}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="py-10 text-center text-gray-500 italic">Aucun mouvement de stock pour ce produit.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal d'ajout de stock */}
            {isInputModalOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
                    <div className="bg-white p-6 rounded-xl shadow-lg w-96">
                        <h3 className="text-xl font-semibold mb-4">Ajouter du Stock</h3>
                        <form onSubmit={handleInput}>
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-bold mb-2">Quantité:</label>
                                <input
                                    type="number"
                                    value={inputQuantity}
                                    onChange={(e) => setInputQuantity(e.target.value)}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    required
                                />
                            </div>
                            <div className="flex justify-end space-x-4">
                                <button type="button" onClick={() => setIsInputModalOpen(false)} className="px-4 py-2 bg-gray-300 rounded-md">Annuler</button>
                                <button type="submit" className="px-4 py-2 bg-green-500 text-white rounded-md">Confirmer</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de retrait de stock */}
            {isOutputModalOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
                    <div className="bg-white p-6 rounded-xl shadow-lg w-96">
                        <h3 className="text-xl font-semibold mb-4">Retirer du Stock</h3>
                        <form onSubmit={handleOutput}>
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-bold mb-2">Quantité:</label>
                                <input
                                    type="number"
                                    value={outputQuantity}
                                    onChange={(e) => setOutputQuantity(e.target.value)}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    required
                                />
                            </div>
                            <div className="flex justify-end space-x-4">
                                <button type="button" onClick={() => setIsOutputModalOpen(false)} className="px-4 py-2 bg-gray-300 rounded-md">Annuler</button>
                                <button type="submit" className="px-4 py-2 bg-red-500 text-white rounded-md">Confirmer</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InventoryPage;
